import React, { Fragment, useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery
} from '@material-ui/core';
import { ExpandLess, ExpandMore } from '@material-ui/icons';
import { ReactComponent as BarwisLogo } from 'assets/logo.svg';
import { ReactComponent as DashboardIcon } from './assets/nav/dashboard.svg';
import { ReactComponent as FormIcon } from './assets/nav/forms.svg';
import { ReactComponent as ReportIcon } from './assets/nav/reports.svg';
import { ReactComponent as CalendarIcon } from './assets/nav/events-manager.svg';
import { ReactComponent as WhistleIcon } from './assets/nav/sports-manager.svg';
import { ReactComponent as ProfileIcon } from './assets/nav/user-manager.svg';
import { ReactComponent as LogoutIcon } from './assets/nav/logout.svg';
import { ReactComponent as WorkoutIcon } from './assets/nav/workout-manager.svg';
import { ReactComponent as DietIcon } from './assets/nav/fueling-manager.svg';
import { ReactComponent as EducationalResourceIcon } from './assets/nav/educational-resources.svg';
import MerchIcon from '@material-ui/icons/Storefront';
import GlobalMessagesIcon from './assets/nav/global-messages-icon.jsx';
import { ReactComponent as VideoIcon } from './assets/nav/video-manager.svg';
import { ReactComponent as FileIcon } from './assets/nav/file-manager.svg';
import { ReactComponent as FeedbackIcon } from './assets/nav/feedbacks.svg';
import { makeStyles } from '@material-ui/core/styles';

import layoutStore from 'stores/layout.store';
import userStore from 'stores/user.store';
import useStyles from './style';
import cx from 'classnames';

const useCustomStyles = makeStyles((theme) => ({
  redChevron: {
    color: 'red'
  }
}));

const Sidebar = () => {
  const [isFormMenuOpen, setIsFormMenuOpen] = useState(false);
  const [isReportMenuOpen, setIsReportMenuOpen] = useState(false);
  const [isWorkoutMenuOpen, setIsWorkoutMenuOpen] = useState(false);
  const [isTestResultsMenuOpen, setTestResultsMenuOpen] = useState(false);
  const classes = useStyles();
  const customClasses = useCustomStyles();
  const { push } = useHistory();
  const { pathname } = useLocation();
  const isDrawerOpen = layoutStore.getIsDrawerOpen();
  const isNotDesktop = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const user = userStore.getUser();
  const userRoleId = +user.role.id;

  const isActive = (basePath) => pathname.startsWith(basePath);

  useEffect(() => {
    if (isNotDesktop) {
      layoutStore.setIsDrawerOpen(false);
    }
  }, [isNotDesktop]);

  return (
    <Drawer variant='permanent' classes={{ paper: classes.drawerPaper }}>
      <Box className={classes.toolbarIcon}>
        {isDrawerOpen && (
          <Fragment>
            <BarwisLogo className={classes.logo} onClick={() => push('/')} />
          </Fragment>
        )}
      </Box>
      <div className={classes.sidebarContainer}>
        <List className={classes.mainSidebar}>
          <ListItem
            button
            onClick={() => push('/')}
            selected={pathname === '/dashboard'}
            className={classes.noHorizontalPadding}>
            <ListItemIcon
              className={cx(classes.listItemIcon, isActive('/dashboard') && classes.activeIcon)}>
              <DashboardIcon />
            </ListItemIcon>
            <ListItemText primary='Dashboard' classes={{ primary: classes.listItemText }} />
          </ListItem>

          <div className={isFormMenuOpen && classes.selectedItem}>
            <ListItem
              button
              onClick={() => setIsFormMenuOpen((value) => !value)}
              className={cx(isFormMenuOpen && classes.selectedItem, classes.noHorizontalPadding)}>
              <ListItemIcon
                className={`${classes.listItemIcon} ${isActive('/forms') ? classes.activeIcon : ''
                  }`}>
                <FormIcon />
              </ListItemIcon>
              <ListItemText primary='Forms' classes={{ primary: classes.listItemText }} />
              {isFormMenuOpen ? (
                <ExpandLess className={customClasses.redChevron} />
              ) : (
                <ExpandMore />
              )}
            </ListItem>

            {isFormMenuOpen && (
              <List component='div' disablePadding>
                {[1, 2, 3, 4].includes(+userRoleId) ? (
                  <ListItem
                    button
                    onClick={() => push('/forms/ars')}
                    selected={isActive('/forms/ars')}
                    className={cx(classes.nested, classes.noHorizontalPadding)}>
                    <ListItemText primary='ARS' classes={{ primary: classes.listItemText }} />
                  </ListItem>
                ) : (
                  ''
                )}
                {userRoleId === 1 ? (
                  <ListItem
                    button
                    onClick={() => push('/forms/ars/config')}
                    selected={isActive('/forms/ars/config')}
                    className={cx(classes.nested, classes.noHorizontalPadding)}>
                    <ListItemText
                      primary='ARS Configuration'
                      classes={{ primary: classes.listItemText }}
                    />
                  </ListItem>
                ) : (
                  ''
                )}
                {[1, 2, 3, 4].includes(userRoleId) ? (
                  <ListItem
                    button
                    onClick={() => push('/forms/hydration')}
                    selected={isActive('/forms/hydration')}
                    className={cx(classes.nested, classes.noHorizontalPadding)}>
                    <ListItemText primary='Hydration' classes={{ primary: classes.listItemText }} />
                  </ListItem>
                ) : (
                  ''
                )}
              </List>
            )}
          </div>


          <div className={isTestResultsMenuOpen && classes.selectedItem}>
            <ListItem
              button
              onClick={() => setTestResultsMenuOpen((value) => !value)}
              className={cx(isFormMenuOpen && classes.selectedItem, classes.noHorizontalPadding)}>
              <ListItemIcon
                className={`${classes.listItemIcon} ${isActive('/testing') ? classes.activeIcon : ''
                  }`}>
                <FormIcon />
              </ListItemIcon>
              <ListItemText primary='Testing' classes={{ primary: classes.listItemText }} />
              {isFormMenuOpen ? (
                <ExpandLess className={customClasses.redChevron} />
              ) : (
                <ExpandMore />
              )}
            </ListItem>

            {isTestResultsMenuOpen && (
              <List component='div' disablePadding>
                {[1, 2, 3, 4].includes(+userRoleId) ? (
                  <ListItem
                    button
                    onClick={() => push('/tests/results')}
                    selected={pathname === '/tests/results'}
                    className={cx(classes.nested, classes.noHorizontalPadding)}>
                    <ListItemText primary='Results' classes={{ primary: classes.listItemText }} />
                  </ListItem>
                ) : (
                  ''
                )}
                {userRoleId === 1 ? (
                  <ListItem
                    button
                    onClick={() => push('/tests/results/create')}
                    selected={pathname === '/tests/results/create'}
                    className={cx(classes.nested, classes.noHorizontalPadding)}>
                    <ListItemText
                      primary='Atlete Performance Tests'
                      classes={{ primary: classes.listItemText }}
                    />
                  </ListItem>
                ) : (
                  ''
                )}
              </List>
            )}
          </div>

          <div className={isReportMenuOpen && classes.selectedItem}>
            <ListItem
              button
              onClick={() => setIsReportMenuOpen((value) => !value)}
              className={cx(isReportMenuOpen && classes.selectedItem, classes.noHorizontalPadding)}>
              <ListItemIcon
                className={`${classes.listItemIcon} ${isActive('/reports') ? classes.activeIcon : ''
                  }`}>
                <ReportIcon />
              </ListItemIcon>
              <ListItemText primary='Reports' classes={{ primary: classes.listItemText }} />
              {isReportMenuOpen ? (
                <ExpandLess className={customClasses.redChevron} />
              ) : (
                <ExpandMore />
              )}
            </ListItem>
            {isReportMenuOpen && (
              <List component='div' disablePadding>
                {[1, 2, 3, 4].includes(userRoleId) ? (
                  <ListItem
                    button
                    onClick={() => push('/reports/readiness-reports/player-readiness-report')}
                    selected={isActive('/reports/readiness-reports/player-readiness-report')}
                    className={cx(classes.nested, classes.noHorizontalPadding)}>
                    <ListItemText
                      primary='Player Readiness Report'
                      classes={{ primary: classes.listItemText }}
                    />
                  </ListItem>
                ) : (
                  ''
                )}
                {[1, 2, 3, 4].includes(userRoleId) ? (
                  <ListItem
                    button
                    onClick={() => push('/reports/readiness-reports/overall-readiness')}
                    selected={isActive('/reports/readiness-reports/overall-readiness')}
                    className={cx(classes.nested, classes.noHorizontalPadding)}>
                    <ListItemText
                      primary='Overall Readiness'
                      classes={{ primary: classes.listItemText }}
                    />
                  </ListItem>
                ) : (
                  ''
                )}
                {[1, 2, 3, 4].includes(+userRoleId) ? (
                  <ListItem
                    button
                    onClick={() => push('/reports/readiness-reports/readiness-progress')}
                    selected={isActive('/reports/readiness-reports/readiness-progress')}
                    className={cx(classes.nested, classes.noHorizontalPadding)}>
                    <ListItemText
                      primary='Readiness Progress'
                      classes={{ primary: classes.listItemText }}
                    />
                  </ListItem>
                ) : (
                  ''
                )}
                {[1, 2, 3, 4].includes(+userRoleId) ? (
                  <>
                    <ListItem
                      button
                      onClick={() => push('/reports/ars-reports/base-report')}
                      selected={isActive('/reports/ars-reports/base-report')}
                      className={cx(classes.nested, classes.noHorizontalPadding)}>
                      <ListItemText
                        primary='ARS Reports'
                        classes={{ primary: classes.listItemText }}
                      />
                    </ListItem>
                    <ListItem
                      button
                      onClick={() => push('/reports/ars-reports/player-comparison')}
                      selected={isActive('/reports/ars-reports/player-comparison')}
                      className={cx(classes.nested, classes.noHorizontalPadding)}>
                      <ListItemText
                        primary='ARS Player Comparison'
                        classes={{ primary: classes.listItemText }}
                      />
                    </ListItem>
                    <ListItem
                      button
                      onClick={() => push('/reports/ars-reports/time-series')}
                      selected={isActive('/reports/ars-reports/time-series')}
                      className={cx(classes.nested, classes.noHorizontalPadding)}>
                      <ListItemText
                        primary='ARS Time Series'
                        classes={{ primary: classes.listItemText }}
                      />
                    </ListItem>
                  </>
                ) : (
                  ''
                )}
              </List>
            )}
          </div>

          <div className={isWorkoutMenuOpen && classes.selectedItem}>
            {[1, 2, 3, 4].includes(userRoleId) ? (
              <ListItem
                button
                className={cx(
                  isWorkoutMenuOpen && classes.selectedItem,
                  classes.noHorizontalPadding
                )}
                onClick={() => setIsWorkoutMenuOpen((value) => !value)}>
                <ListItemIcon
                  className={`${classes.listItemIcon} ${isActive('/workout-manager') && classes.activeIcon
                    }`}>
                  <WorkoutIcon />
                </ListItemIcon>
                <ListItemText
                  primary='Workout Manager'
                  classes={{ primary: classes.listItemText }}
                />
                {isWorkoutMenuOpen ? (
                  <ExpandLess className={customClasses.redChevron} />
                ) : (
                  <ExpandMore />
                )}
              </ListItem>
            ) : (
              ''
            )}
            {isWorkoutMenuOpen && (
              <List component='div' disablePadding>
                <ListItem
                  button
                  className={`${classes.nested} ${classes.noHorizontalPadding}`}
                  onClick={() => push('/workout-manager/exercises')}
                  selected={isActive('/workout-manager/exercises')}>
                  <ListItemText primary='Exercises' classes={{ primary: classes.listItemText }} />
                </ListItem>
                <ListItem
                  button
                  className={`${classes.nested} ${classes.noHorizontalPadding}`}
                  onClick={() => push('/workout-manager/library')}
                  selected={isActive('/workout-manager/library')}>
                  <ListItemText primary='Library' classes={{ primary: classes.listItemText }} />
                </ListItem>
                <ListItem
                  button
                  className={`${classes.nested} ${classes.noHorizontalPadding}`}
                  onClick={() => push('/workout-manager/workout-assignments')}
                  selected={isActive('/workout-manager/workout-assignments')}>
                  <ListItemText
                    primary='Workout Assignments'
                    classes={{ primary: classes.listItemText }}
                  />
                </ListItem>
                <ListItem
                  button
                  className={`${classes.nested} ${classes.noHorizontalPadding}`}
                  onClick={() => push('/workout-manager/phases')}
                  selected={isActive('/workout-manager/phases')}>
                  <ListItemText primary='Phases' classes={{ primary: classes.listItemText }} />
                </ListItem>
              </List>
            )}
          </div>

          {[1, 2].includes(userRoleId) ? (
            <ListItem
              button
              onClick={() => push('/educational-resources')}
              selected={pathname === '/educational-resources'}
              className={classes.noHorizontalPadding}>
              <ListItemIcon
                className={`${classes.listItemIcon} ${isActive('/educational-resources') ? classes.activeIcon : ''
                  }`}>
                <EducationalResourceIcon />
              </ListItemIcon>
              <ListItemText
                primary='Educational Resources'
                classes={{ primary: classes.listItemText }}
              />
            </ListItem>
          ) : (
            ''
          )}

          {[1, 2].includes(userRoleId) ? (
            <ListItem
              button
              onClick={() => push('/merch-promotion')}
              selected={pathname === '/merch-promotion'}
              className={classes.noHorizontalPadding}>
              <ListItemIcon
                className={`${classes.listItemIcon} ${isActive('/merch-promotion') ? classes.activeIcon : ''
                  }`}>
                <MerchIcon />
              </ListItemIcon>
              <ListItemText
                primary='Merch Promotion'
                classes={{ primary: classes.listItemText }}
              />
            </ListItem>
          ) : (
            ''
          )}

          {[1, 2].includes(userRoleId) ? (
            <ListItem
              button
              onClick={() => push('/global-messages')}
              selected={pathname === '/global-messages'}
              className={classes.noHorizontalPadding}>
              <ListItemIcon
                className={`${classes.listItemIcon} ${isActive('/global-messages') ? classes.activeIcon : ''
                  }`}>
                <GlobalMessagesIcon />
              </ListItemIcon>
              <ListItemText primary='Global Messages' classes={{ primary: classes.listItemText }} />
            </ListItem>
          ) : (
            ''
          )}
        </List>
        <List className={classes.bottomSidebar} style={{ paddingBottom: '0.75rem' }}>
          {[1, 2, 3, 4].includes(userRoleId) ? (
            <ListItem
              button
              onClick={() => push('/fueling-manager/fueling-plans')}
              selected={pathname === '/fueling-manager/fueling-plans'}
              className={`${classes.noHorizontalPadding}`}>
              <ListItemIcon
                className={`${classes.listItemIcon} ${isActive('/fueling-manager/fueling-plans') ? classes.activeIcon : ''
                  }`}>
                <DietIcon />
              </ListItemIcon>
              <ListItemText primary='Fueling Manager' classes={{ primary: classes.listItemText }} />
            </ListItem>
          ) : (
            ''
          )}
          {userRoleId === 1 ? (
            <ListItem
              button
              onClick={() => push('/video-manager/video-feeds')}
              selected={pathname === '/video-manager/video-feeds'}
              className={classes.noHorizontalPadding}>
              <ListItemIcon
                className={`${classes.listItemIcon} ${isActive('/video-manager/video-feeds') ? classes.activeIcon : ''
                  }`}>
                <VideoIcon />
              </ListItemIcon>
              <ListItemText primary='Video Manager' classes={{ primary: classes.listItemText }} />
            </ListItem>
          ) : (
            ''
          )}
          {[1, 2, 3, 4].includes(+userRoleId) ? (
            <ListItem
              button
              onClick={() => push('/file-manager')}
              selected={pathname === '/file-manager'}
              className={classes.noHorizontalPadding}>
              <ListItemIcon
                className={`${classes.listItemIcon} ${isActive('/file-manager') ? classes.activeIcon : ''
                  }`}>
                <FileIcon />
              </ListItemIcon>
              <ListItemText primary='File Manager' classes={{ primary: classes.listItemText }} />
            </ListItem>
          ) : (
            ''
          )}
          {/* <ListItem button onClick={() => push('/')}>
              <ListItemIcon>
                <CalendarIcon />
              </ListItemIcon>
              <ListItemText primary='Calendar' />
            </ListItem>
            <ListItem button onClick={() => push('/')}>
              <ListItemIcon>
                <MessageIcon />
              </ListItemIcon>
              <ListItemText primary='Message' />
            </ListItem> */}
          <ListItem
            button
            onClick={() => push('/event-manager')}
            selected={pathname === '/event-manager'}
            className={classes.noHorizontalPadding}>
            <ListItemIcon
              className={`${classes.listItemIcon} ${isActive('/event-manager') ? classes.activeIcon : ''
                }`}>
              <CalendarIcon />
            </ListItemIcon>
            <ListItemText primary='Events Manager' classes={{ primary: classes.listItemText }} />
          </ListItem>
          {[1, 2].includes(+userRoleId) ? (
            <ListItem
              button
              onClick={() => push('/sport-management')}
              selected={pathname === '/sport-management'}
              className={classes.noHorizontalPadding}>
              <ListItemIcon
                className={`${classes.listItemIcon} ${isActive('/sport-management') ? classes.activeIcon : ''
                  }`}>
                <WhistleIcon />
              </ListItemIcon>
              <ListItemText primary='Sports Manager' classes={{ primary: classes.listItemText }} />
            </ListItem>
          ) : (
            ''
          )}
          {[1, 2, 3].includes(userRoleId) ? (
            <ListItem
              button
              onClick={() => push('/user-management')}
              selected={pathname === '/user-management'}
              className={classes.noHorizontalPadding}>
              <ListItemIcon
                className={`${classes.listItemIcon} ${isActive('/user-management') ? classes.activeIcon : ''
                  }`}>
                <ProfileIcon />
              </ListItemIcon>
              <ListItemText primary='User Manager' classes={{ primary: classes.listItemText }} />
            </ListItem>
          ) : (
            ''
          )}
          {/* <ListItem button onClick={() => push('/')}>
              <ListItemIcon>
                <DataManagementIcon />
              </ListItemIcon>
              <ListItemText primary='Data Management' />
            </ListItem> */}
          {userRoleId === 1 ? (
            <ListItem
              button
              onClick={() => push('/feedbacks')}
              selected={pathname === '/feedbacks'}
              className={classes.noHorizontalPadding}>
              <ListItemIcon
                className={`${classes.listItemIcon} ${isActive('/feedbacks') ? classes.activeIcon : ''
                  }`}>
                <FeedbackIcon />
              </ListItemIcon>
              <ListItemText primary='Feedbacks' classes={{ primary: classes.listItemText }} />
            </ListItem>
          ) : (
            ''
          )}
          <ListItem
            button
            onClick={async () => await userStore.logout()}
            className={cx(classes.noHorizontalPadding, classes.logoutButton)}>
            <ListItemIcon className={classes.listItemIcon}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary='Logout' classes={{ primary: classes.listItemText }} />
          </ListItem>
        </List>
      </div>
    </Drawer>
  );
};

export default observer(Sidebar);
